/** The three statuses a call label can carry, and the colours that name them.
 *
 *  Status is written per call by the duty's own label-mapping prompt into
 *  `custom_post_call_analysis`, alongside the label itself. Unlike labels — free
 *  text with no registry — this *is* a closed set of three, which is what makes
 *  colouring it semantically correct rather than a lookup table pretending to be
 *  one. Anything outside the three reads as unknown, never as a fourth status.
 *
 *  Shared rather than local to `features/calls` because the metrics breakdown
 *  bars colour by the same statuses. Two copies of these values would drift the
 *  first time one of them was tuned, and a label pill that disagreed with the
 *  bar for the same status is worse than no colour at all.
 */

/** Wire values exactly as they appear in the column — lower-cased on arrival,
 *  never title-cased, because the comparison is against what the LLM wrote. */
export const LABEL_STATUSES = ['done', 'in review', 'retry'] as const

export type LabelStatus = (typeof LABEL_STATUSES)[number]

export interface LabelStatusTone {
  /** Badge background tint. */
  bg: string
  /** Text drawn on `bg`. */
  text: string
  /** Breakdown-bar fill. A raw CSS colour, not a Tailwind class: the bar's
   *  width is already an inline style, so its colour travels the same way.
   *  Deliberately the ramp's step-3 UI-background slot — the bar sits *behind*
   *  the row's text, so it has to stay a tint rather than become a fill. */
  bar: string
  /** The same bar under the cursor: step 5 of the same ramp. Saturating the bar
   *  itself, rather than tinting the row behind it, keeps the highlight inside
   *  the shape being read — a neutral wash across the row would paint grey over
   *  precisely the part of the row the bar does not reach, which reads as a
   *  second, competing bar. Two steps up is enough to register without pushing
   *  the tint dark enough to fight the text over it. */
  barHover: string
}

const TONES: Record<LabelStatus, LabelStatusTone> = {
  done: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    bar: 'var(--green-3)',
    barHover: 'var(--green-5)',
  },
  'in review': {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    bar: 'var(--purple-3)',
    barHover: 'var(--purple-5)',
  },
  retry: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    bar: 'var(--orange-3)',
    barHover: 'var(--orange-5)',
  },
}

/** What a status outside the three reads as. Neutral on purpose: a call nobody
 *  categorised is not a fourth outcome, and guessing one would be a claim the
 *  data does not make.
 *
 *  Two greys rather than one, carried over from the badge this was lifted out
 *  of: a value that is absent tints one step darker than one that is present
 *  but unrecognised. The distinction is almost certainly accidental, but it is
 *  live in the Calls table today and folding it away here would be a pixel
 *  change nobody asked for — so it is preserved rather than tidied. The bar
 *  colour is shared, because a one-step grey difference does not survive being
 *  drawn as a tint behind text. */
export const UNKNOWN_LABEL_STATUS_TONE: LabelStatusTone = {
  bg: 'bg-gray-50',
  text: 'text-gray-700',
  bar: 'var(--primary-ui-bg)',
  barHover: 'var(--indigo-5)',
}

const ABSENT_LABEL_STATUS_TONE: LabelStatusTone = {
  ...UNKNOWN_LABEL_STATUS_TONE,
  bg: 'bg-gray-100',
}

/** Narrows a wire value to one of the three, or `null` when it is neither.
 *
 *  Case-insensitive and whitespace-trimmed: the value is model-written, so
 *  "Done" and "done" are the same outcome and neither should fall through to
 *  the unknown tone over presentation.
 */
export function toLabelStatus(
  value: string | null | undefined
): LabelStatus | null {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  return (LABEL_STATUSES as readonly string[]).includes(normalized)
    ? (normalized as LabelStatus)
    : null
}

/** The tone for a wire status value, falling back to neutral. */
export function labelStatusTone(
  value: string | null | undefined
): LabelStatusTone {
  if (!value) return ABSENT_LABEL_STATUS_TONE
  const status = toLabelStatus(value)
  return status ? TONES[status] : UNKNOWN_LABEL_STATUS_TONE
}
